import React, { useEffect, useState } from 'react';
import useApi from '../../hooks/useApi';
import { v4 as uuidv4 } from 'uuid';

/*
 Props:
  - transfer: existing transfer object when editing (optional)
  - onClose: fn to call to close form/modal
  - onSaveSuccess: fn called with saved transfer after successful save
*/
const InventoryTransferForm = (props) => {
  // accept multiple possible prop names from different list components
  const {
    transfer = null,
    record = null,
    initialValues = null,
    initialData = null,
    data = null,
    row = null,
    item = null,
    defaultValues = null,
    onClose = () => {},
    onSaveSuccess = () => {}
  } = props;
  // determine source object (prefer explicit transfer/record)
  let sourceRecord = transfer || record || initialValues || initialData || data || row || item || defaultValues || null;
  // If not explicitly provided, detect the first prop value that looks like a transfer object
  if (!sourceRecord) {
    for (const v of Object.values(props)) {
      if (v && typeof v === 'object') {
        if (v.fromLocation || v.toLocation || Array.isArray(v.items) || v.fromLocationId || v.toLocationId) {
          sourceRecord = v;
          break;
        }
      }
    }
  }
   const api = useApi();
 
   const [stores, setStores] = useState([]);
   const [warehouses, setWarehouses] = useState([]);
   const [products, setProducts] = useState([]);
 
   const [loading, setLoading] = useState(false);
   const [saving, setSaving] = useState(false);
   const [error, setError] = useState(null);
 
   const [fromType, setFromType] = useState('store');
   const [toType, setToType] = useState('warehouse');
   const [fromId, setFromId] = useState('');
   const [toId, setToId] = useState('');
   const [notes, setNotes] = useState('');
   const [items, setItems] = useState([{ id: uuidv4(), product: '', quantity: '' }]);

  // load dropdowns once
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [storesRes, warehousesRes, productsRes] = await Promise.all([
          api.get('stores', { queryParams: { limit: 1000 } }),
          api.get('warehouses', { queryParams: { limit: 1000 } }),
          api.get('products', { queryParams: { limit: 1000 } })
        ]);
        setStores(storesRes?.data || storesRes || []);
        setWarehouses(warehousesRes?.data || warehousesRes || []);
        setProducts(productsRes?.data || productsRes || []);
      } catch (err) {
        console.error('Failed loading form dropdowns', err);
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line
  }, []);

  // populate when editing / when dropdowns load
  useEffect(() => {
    const initial = sourceRecord;
    if (!initial) return;
    // If dropdowns not loaded yet, still populate basic IDs; rerun when products/stores arrive
    setFromType(initial.fromLocation?.type || initial.fromType || 'store');
    setToType(initial.toLocation?.type || initial.toType || 'warehouse');
    setFromId(initial.fromLocation?.id || initial.fromLocationId || initial.fromId || '');
    setToId(initial.toLocation?.id || initial.toLocationId || initial.toId || '');
    setNotes(initial.notes || initial.comment || '');

    if (Array.isArray(initial.items) && initial.items.length > 0) {
      const mapped = initial.items.map(i => {
        let prodId = '';
        if (i.product) {
          prodId = (typeof i.product === 'string') ? i.product : (i.product._id || i.product.id || '');
        } else if (i.productId) {
          prodId = i.productId;
        }
        return {
          id: uuidv4(),
          product: prodId,
          quantity: i.quantity ?? i.qty ?? ''
        };
      });
      setItems(mapped);
    } else {
      setItems([{ id: uuidv4(), product: '', quantity: '' }]);
    }
  // re-run when source or dropdown lists change (ensures product IDs map correctly after loads)
  }, [sourceRecord, stores.length, warehouses.length, products.length]);

  const addItem = () => setItems(prev => [...prev, { id: uuidv4(), product: '', quantity: '' }]);
  const removeItem = (id) => setItems(prev => prev.filter(i => i.id !== id));
  const updateItem = (id, field, value) => setItems(prev => prev.map(i => i.id === id ? { ...i, [field]: value } : i));

  const validate = () => {
    setError(null);
    if (!fromId) return 'From location required';
    if (!toId) return 'To location required';
    if (fromType === toType && fromId === toId) return 'From and To must differ';
    const validItems = items.filter(i => i.product && Number(i.quantity) > 0);
    if (validItems.length === 0) return 'Add at least one item with positive quantity';
    return null;
  };

  const handleSave = async () => {
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setSaving(true);
    setError(null);

    const payload = {
      fromLocation: { type: fromType, id: fromId },
      toLocation: { type: toType, id: toId },
      items: items.filter(i => i.product && Number(i.quantity) > 0).map(i => ({ product: i.product, quantity: Number(i.quantity) })),
      notes: notes || undefined
    };

    try {
      let resp;
      const existingId = sourceRecord && (sourceRecord._id || sourceRecord.id);
      if (existingId) {
        resp = await api.put(`inventory-transfers/${existingId}`, payload);
      } else {
        resp = await api.post('inventory-transfers', payload);
      }
       const saved = resp?.data ?? resp;
       onSaveSuccess(saved);
       onClose();
     } catch (err) {
       console.error('Save failed', err);
       setError(err?.message || 'Save failed');
     } finally {
       setSaving(false);
     }
   };

  return (
    <div className="p-4 max-w-3xl">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="p-3 border rounded bg-white">
          <label className="text-sm font-medium">From (type)</label>
          <select className="mt-1 block w-full border rounded p-2" value={fromType} onChange={(e) => { setFromType(e.target.value); setFromId(''); }}>
            <option value="store">Store</option>
            <option value="warehouse">Warehouse</option>
          </select>

          <label className="text-sm font-medium mt-3">From (location)</label>
          <select className="mt-1 block w-full border rounded p-2" value={fromId} onChange={(e) => setFromId(e.target.value)}>
            <option value="">Select</option>
            {(fromType === 'store' ? stores : warehouses).map(loc => (
              <option key={loc._id || loc.id} value={loc._id || loc.id}>{loc.name || loc.storeName || loc.code || (loc._id || loc.id)}</option>
            ))}
          </select>
        </div>

        <div className="p-3 border rounded bg-white">
          <label className="text-sm font-medium">To (type)</label>
          <select className="mt-1 block w-full border rounded p-2" value={toType} onChange={(e) => { setToType(e.target.value); setToId(''); }}>
            <option value="warehouse">Warehouse</option>
            <option value="store">Store</option>
          </select>

          <label className="text-sm font-medium mt-3">To (location)</label>
          <select className="mt-1 block w-full border rounded p-2" value={toId} onChange={(e) => setToId(e.target.value)}>
            <option value="">Select</option>
            {(toType === 'store' ? stores : warehouses).map(loc => (
              <option key={loc._id || loc.id} value={loc._id || loc.id}>{loc.name || loc.storeName || loc.code || (loc._id || loc.id)}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="p-3 border rounded bg-white mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-semibold">Items</div>
          <button type="button" onClick={addItem} className="px-2 py-1 bg-green-600 text-white rounded text-sm">+ Add Item</button>
        </div>

        <div className="space-y-2">
          {items.map((it) => (
            <div key={it.id} className="grid grid-cols-12 gap-2 items-center">
              <div className="col-span-7">
                <select className="w-full border rounded p-2" value={it.product} onChange={(e) => updateItem(it.id, 'product', e.target.value)}>
                  <option value="">Select product</option>
                  {products.map(p => (
                    <option key={p._id || p.id} value={p._id || p.id}>{p.name || p.sku || (p._id || p.id)}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-3">
                <input className="w-full border rounded p-2" type="number" min="0" value={it.quantity} onChange={(e) => updateItem(it.id, 'quantity', e.target.value)} placeholder="Quantity" />
              </div>
              <div className="col-span-2 flex justify-end">
                <button type="button" onClick={() => removeItem(it.id)} className="px-2 py-1 border rounded text-sm">Remove</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="p-3 border rounded bg-white mb-4">
        <label className="text-sm font-medium">Notes (optional)</label>
        <textarea className="mt-1 block w-full border rounded p-2" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
      </div>

      {error && <div className="text-sm text-red-600 mb-3">{error}</div>}

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="px-3 py-1 border rounded">Cancel</button>
        <button type="button" onClick={handleSave} disabled={saving} className="px-4 py-2 bg-indigo-600 text-white rounded">
          {saving ? 'Saving...' : 'Save Transfer'}
        </button>
      </div>
    </div>
  );
};

export default InventoryTransferForm;